import {
  EvaluatedVideoSource,
  EvaluatedVideoStreamSource,
  VideoSourceType,
} from "@videosmart/player-template";
import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { first } from "rxjs/operators";

import { EndpointActions, VideoActions } from "../redux/actions";
import { RootState } from "../redux/models";
import { selectCurrentVideoSrc } from "../redux/selectors";
import { frameUpdateSubject } from "../services";
import { getAmazonS3Url, raf, hasValue, caf } from "../utils";
import styles from "./Video.module.scss";
import { fromEvent } from "rxjs";
import Hls from "hls.js";
import { QualityActions } from "../redux/actions";
import { ErrorType } from "../enums";

export const videoRef = React.createRef<Video>();

export interface VideoProps {
  logIsEnded: typeof VideoActions.actionCreators.logIsEnded;
  logIsLoaded: typeof VideoActions.actionCreators.logIsLoaded;
  logIsPlaying: typeof VideoActions.actionCreators.logIsPlaying;
  logMediaError: typeof EndpointActions.actionCreators.logMediaError;
  playerError: typeof EndpointActions.actionCreators.logPlayerError;
  logProgress: typeof VideoActions.actionCreators.logProgress;
  logTimeUpdate: typeof VideoActions.actionCreators.logTimeUpdate;
  logWaiting: typeof VideoActions.actionCreators.logWaiting;
  pause: typeof VideoActions.actionCreators.pause;
  play: typeof VideoActions.actionCreators.play;
  signAsset: typeof EndpointActions.actionCreators.signAsset;
  logIsPartialEnd: typeof VideoActions.actionCreators.logIsPartialEnd;
  logViewedUpdate: typeof VideoActions.actionCreators.logViewedUpdate;
  setQualityAvailable: typeof QualityActions.actionCreators.setAvailable;
  setQualityLevels: typeof QualityActions.actionCreators.setLevels;

  qualityAuto: boolean;
  qualityCurrentLevel: number;
  isLooped: boolean;
  isMuted: boolean;
  isPlaying: boolean;
  videoSource?: EvaluatedVideoSource;
  volume: number;
  autoPlay: boolean;
}
interface VideoState {
  error?: MediaError;
  errorTime: number;
  frameRate: number;
  hasError: boolean;
  source?: string;
  hasPartialEnd: boolean;
  hasViewed20Percent: boolean;
  hasViewed40Percent: boolean;
  hasViewed60Percent: boolean;
  hasLoadedData: boolean;
  isHls: boolean;
}

class Video extends Component<VideoProps, VideoState> {
  private _currentFrame: number;

  private _rafToken: number;

  private _videoRef: React.RefObject<HTMLVideoElement>;

  private _hls?: Hls;

  private _hlsManifestDuration?: number;

  private _isMounted: boolean = false;

  private _isHlsExpired: boolean = false;

  private safeSetState = (state: any, callback?: () => void) => {
    if (!this._isMounted) {
      return;
    }
    this.setState(state, callback);
  };

  constructor(props: VideoProps) {
    super(props);

    this.state = {
      error: undefined,
      errorTime: 0,
      frameRate: 25,
      hasError: false,
      source: undefined,
      hasPartialEnd: false,
      hasViewed20Percent: false,
      hasViewed40Percent: false,
      hasViewed60Percent: false,
      hasLoadedData: false,
      isHls: false,
    };

    this._currentFrame = 0;
    this._rafToken = 0;
    this._videoRef = React.createRef();
    this._hlsManifestDuration = undefined;
  }

  public get isPlaying(): boolean {
    if (this._videoRef.current) {
      return !this._videoRef.current.paused;
    } else {
      return false;
    }
  }

  public get currentTime(): number {
    if (this._videoRef.current) {
      return this._videoRef.current.currentTime;
    } else {
      return 0;
    }
  }

  public set currentTime(value: number) {
    if (this._videoRef.current) {
      this._videoRef.current.currentTime = value;
    }
  }

  public componentDidUpdate = (prevProps: VideoProps) => {
    if (this._videoRef.current) {
      const {
        isLooped,
        isMuted,
        videoSource,
        volume,
        autoPlay,
        qualityAuto,
        qualityCurrentLevel,
      } = this.props;
      const video = this._videoRef.current;

      if (isLooped !== prevProps.isLooped) {
        video.loop = this.props.isLooped;
      }

      if (isMuted !== prevProps.isMuted) {
        video.muted = this.props.isMuted;
      }

      if (volume !== prevProps.volume) {
        video.volume = this.props.volume;
      }

      if (videoSource !== prevProps.videoSource) {
        this.setVideoSource(videoSource);
      }

      if (autoPlay !== prevProps.autoPlay) {
        video.autoplay = this.props.autoPlay;
      }

      // Apply smooth quality switching based on Redux selection
      if (
        this._hls &&
        (qualityAuto !== prevProps.qualityAuto ||
          qualityCurrentLevel !== prevProps.qualityCurrentLevel)
      ) {
        if (qualityAuto) {
          // Auto ABR: let hls.js choose level dynamically (-1)
          this._hls.nextLevel = -1;
        } else if (typeof qualityCurrentLevel === "number") {
          // Fixed quality: force specific level index
          this._hls.nextLevel = qualityCurrentLevel;
        }
      }
    }
  };

  public componentWillUnmount = () => {
    this._isMounted = false;
    if (this._rafToken) {
      try {
        caf(this._rafToken);
      } catch {
        // ignore
      }
      this._rafToken = 0;
    }
    this.destroyHls();
  };

  public load = () => {
    if (this._videoRef.current) {
      this._videoRef.current.load();
    }
  };

  public pause = () => {
    if (this._videoRef.current) {
      this._videoRef.current.pause();
    }
  };

  public play = () => {
    if (this._videoRef.current) {
      const playPromise = this._videoRef.current.play();
      // Play is not a promise on older browsers
      if (hasValue(playPromise) && typeof playPromise.then === "function") {
        playPromise.then(undefined, (e: DOMException) => {
          // https://developers.google.com/web/updates/2017/06/play-request-was-interrupted
          if (
            this._videoRef.current &&
            (e.name === "AbortError" || (e.ABORT_ERR && e.code === e.ABORT_ERR))
          ) {
            const subscription = fromEvent(this._videoRef.current, "loadstart")
              .pipe(first())
              .subscribe(() => {
                this.play();
                subscription.unsubscribe();
              });
          }
        });
      }
    }
  };

  public render = () => {
    return (
      <video
        disablePictureInPicture
        ref={this._videoRef}
        className={styles["root"]}
        controls={false}
        controlsList={"nodownload nofullscreen noplaybackrate"}
        crossOrigin={"anonymous"}
        disableRemotePlayback
        onContextMenu={(e) => e.preventDefault()}
        onDurationChange={this.handleDurationChange}
        onEnded={this.handleEnded}
        onError={this.handleError}
        onLoadedData={this.handleLoadedData}
        onLoadedMetadata={this.handleLoadedData}
        onCanPlay={this.handleLoadedData}
        onPause={this.handlePause}
        onPlay={this.handlePlay}
        onProgress={this.handleProgress}
        onSeeked={this.handleSeeked}
        onTimeUpdate={this.handleTimeUpdate}
        onWaiting={this.handleWaiting}
        playsInline={true}
        preload={this.props.autoPlay ? "auto" : "metadata"}
        autoPlay={this.props.autoPlay}
        muted={this.props.isMuted}
        loop={this.props.isLooped}
        src={this.state.source}
      />
    );
  };

  public seekTo = (time: number) => {
    this.currentTime = time;
    this.frameUpdateCheck();
  };

  private frameUpdateCheck = () => {
    const { frameRate } = this.state;

    const time = this.currentTime;
    const frame = Math.floor(time * frameRate);
    if (this._currentFrame !== frame) {
      this._currentFrame = frame;
      frameUpdateSubject.next({ currentFrame: frame, currentTime: time });
    }

    // Schedule next frame check if still playing
    this._rafToken = 0;
    if (this.props.isPlaying) {
      this.requestFrameUpdateCheck();
    }
  };

  private handleDurationChange = () => {
    this.handleProgress();
  };

  private handleEnded = () => {
    this.props.logIsEnded(true);
  };

  private handleError = () => {
    const { hasError } = this.state;

    if (!hasError) {
      console.warn("Trying to resolve video...");

      this.safeSetState(() => ({
        error:
          (this._videoRef.current && this._videoRef.current.error) || undefined,
        errorTime:
          (this._videoRef.current && this._videoRef.current.currentTime) || 0,
        hasError: true,
      }));

      this.setVideoSource(this.props.videoSource);

      let errorType = ErrorType.Unknown;
      if (this._isHlsExpired) {
        errorType = ErrorType.Expired;
        this.props.playerError({
          type: errorType,
          devMessage: "Video source has expired",
        });
      } else if (this.state.error) {
        this.props.logMediaError(this.state.error);
      } else {
        this.props.playerError({
          type: errorType,
          devMessage: "An unknown error occurred",
        });
      }
    }
  };

  private handleLoadedData = () => {
    // If the video is not loaded yet, we will wait for the next frame update or has been loaded already
    if (this._videoRef.current && !this.state.hasLoadedData) {
      const video = this._videoRef.current;

      if (this.state.hasError) {
        video.currentTime = this.state.errorTime;
        video.play();

        this.safeSetState(() => ({
          error: undefined,
          errorTime: 0,
          hasError: false,
        }));
      }

      const height = video.videoHeight;
      const width = video.videoWidth;

      if (height === 0 && width === 0) {
        window.setTimeout(() => this.handleLoadedData(), 100);
        return;
      }

      // Mark hasLoaded state to avoid multiple calls
      this.safeSetState(() => ({
        hasLoadedData: true,
      }));

      const duration = this.getResolvedDuration();

      this.props.logIsLoaded({
        aspectRatio: width / height,
        duration,
        isHls: this.state.isHls,
      });

      this.handleProgress();
    }
  };

  private handlePause = () => {
    if (this._videoRef.current) {
      if (
        Math.abs(this.currentTime - this._videoRef.current.duration) < 0.00001
      ) {
        //Avoid to trigger the pause event when scene has ended
        return;
      }
    }
    this.props.logIsPlaying(false);
    this.frameUpdateCheck();
  };

  private handlePlay = () => {
    this.props.logIsPlaying(true);

    this.frameUpdateCheck();
  };

  private handleProgress = () => {
    if (this._videoRef.current) {
      const { buffered } = this._videoRef.current;

      const bufferedTimes: [number, number][] = [];

      for (let i = 0; i < buffered.length; i++) {
        bufferedTimes.push([buffered.start(i), buffered.end(i)]);
      }

      this.props.logProgress(bufferedTimes);
      this.handleViewedUpdate();
    }
  };

  private handleSeeked = () => {
    this.frameUpdateCheck();
  };

  private handleTimeUpdate = (
    e: React.SyntheticEvent<HTMLVideoElement, Event>,
  ) => {
    const currentTime = e.currentTarget.currentTime;
    this.props.logTimeUpdate(currentTime);

    this.frameUpdateCheck();
    this.handleViewedUpdate();
  };

  private handleWaiting = () => {
    this.props.logWaiting();
  };

  private requestFrameUpdateCheck = () => {
    // Prevent concurrent requests
    if (this._rafToken === 0) {
      this._rafToken = raf(this.frameUpdateCheck);
    }
  };

  private setVideoSource = async (videoSource?: EvaluatedVideoSource) => {
    // Reset HLS expiry flag when changing source, so we can accurately report if the new source is expired or not
    this._isHlsExpired = false;
    if (videoSource) {
      this.safeSetState(() => ({
        hasLoadedData: false,
      }));

      let url: string | undefined;
      const streamSource = this.getActiveStream(videoSource.stream);

      switch (videoSource.type) {
        case VideoSourceType.S3: {
          if (videoSource.isPrivate) {
            try {
              const { url: signedUrl } = await this.props.signAsset({
                s3Key: videoSource.s3Key,
                s3Bucket: videoSource.s3Bucket,
                s3Region: videoSource.s3Region,
              });
              url = signedUrl;
            } catch {
              // url remains undefined; streamSource fallback below will handle it.
              // Only report the error if there is no stream fallback to recover with.
              if (!streamSource) {
                this.props.playerError({
                  type: ErrorType.SignRequestFailed,
                  devMessage: `Failed to sign asset for playerId: ${videoSource.s3Key}, no stream fallback available`,
                });
              }
            }
          } else {
            url = getAmazonS3Url(videoSource.s3Bucket, videoSource.s3Key);
          }
          break;
        }
        case VideoSourceType.Url: {
          url = videoSource.source;
          break;
        }
      }

      // If a non-expired stream is available, treat it as a fallback when the primary S3/URL target is unreachable
      if (
        streamSource &&
        (videoSource.type === VideoSourceType.S3 ||
          videoSource.type === VideoSourceType.Url)
      ) {
        if (url) {
          const isPrimaryReachable = await this.isSourceReachable(url);
          if (!isPrimaryReachable) {
            url = streamSource.source;
          }
        } else {
          url = streamSource.source;
        }
      }

      this.safeSetState(() => ({
        frameRate: videoSource.frameRate,
      }));

      if (url) {
        this.setupMediaSource(url);
      } else {
        this.destroyHls();
        this.safeSetState(() => ({ source: undefined }));
      }
    } else {
      this.destroyHls();
      this.safeSetState(() => ({
        source: undefined,
      }));
    }
  };

  private getActiveStream = (stream?: EvaluatedVideoStreamSource) => {
    // Streams from the template can expire; only use entries that either have no expiry or whose expiry is still in the future
    if (!stream) {
      return undefined;
    }

    if (!stream.expiryDate) {
      return stream;
    }

    const expiry = Date.parse(stream.expiryDate);

    if (Number.isNaN(expiry)) {
      // If expiryDate is invalid, ignore it and use the stream
      return stream;
    }

    if (expiry <= Date.now()) {
      this._isHlsExpired = true;
      return undefined;
    }

    return stream;
  };

  private isSourceReachable = async (url: string): Promise<boolean> => {
    // Presigned S3 URLs are signed for GET, so a HEAD request against them fails with 403; probe with a ranged GET instead.
    const controller =
      typeof AbortController !== "undefined"
        ? new AbortController()
        : undefined;
    const timeoutId = controller
      ? window.setTimeout(() => controller.abort(), 3000)
      : undefined;
    try {
      const response = await this.probeMediaResponse(url, controller?.signal);
      return response?.ok ?? false;
    } finally {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    }
  };

  private probeMediaResponse = async (
    url: string,
    signal?: AbortSignal,
  ): Promise<Response | undefined> => {
    try {
      return await fetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-store",
        headers: {
          Range: "bytes=0-0",
        },
        signal,
      });
    } catch (error) {
      return undefined;
    }
  };

  private isHlsUrl = (url: string) => /\.m3u8($|\?)/i.test(url);

  private destroyHls = () => {
    if (this._hls) {
      try {
        this._hls.destroy();
      } catch {
        // ignore
      }
      this._hls = undefined;
    }
    this._hlsManifestDuration = undefined;
    this.props.setQualityAvailable(false);
    this.props.setQualityLevels([]);
  };

  private setupMediaSource = (url: string) => {
    const video = this._videoRef.current;
    if (!video) {
      this.safeSetState(() => ({ source: undefined }));
      return;
    }

    // Always reset previous state
    this.destroyHls();

    if (this.isHlsUrl(url)) {
      // Prefer native HLS when available (Safari, some iOS browsers)
      const canNativeHls =
        typeof video.canPlayType === "function" &&
        (video.canPlayType("application/vnd.apple.mpegURL") ||
          video.canPlayType("application/x-mpegURL"));

      if (canNativeHls) {
        // Use browser's native HLS pipeline
        this.safeSetState(() => ({ source: url, isHls: true }));
        this.props.setQualityAvailable(true);
        this.props.setQualityLevels([]);
        return;
      }

      if (Hls.isSupported()) {
        // Ensure the video tag doesn't have a competing src before attaching MSE
        try {
          video.removeAttribute("src");
          video.load();
        } catch {
          // ignore
        }

        const hls = new Hls({
          // Optimize for lower end-to-end latency on live/LL-HLS streams
          lowLatencyMode: true,
          // Do not pick video levels larger than the current player size (saves bandwidth/CPU)
          capLevelToPlayerSize: true,
          // Initial quality selection: -1 lets hls.js choose automatically (ABR)
          startLevel: -1,
          // Offload transmuxing/parsing to a Web Worker to reduce main-thread jank
          enableWorker: true,
          // Amount of already-played media to keep in buffer (seconds) to control memory usage
          backBufferLength: 60,
        });
        this._hls = hls;
        hls.attachMedia(video);
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          hls.loadSource(url);
        });
        hls.on(Hls.Events.LEVEL_LOADED, (_event, data) => {
          const manifestDuration = data?.details?.totalduration;
          if (
            typeof manifestDuration === "number" &&
            Number.isFinite(manifestDuration) &&
            manifestDuration > 0
          ) {
            this._hlsManifestDuration = manifestDuration;
          }
        });

        // Optional: start playback automatically if requested once manifest is parsed
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (!this._isMounted) return;
          const levels = (hls.levels || []).map((lvl, idx) => ({
            index: idx,
            height: (lvl as any).height,
            bitrate: (lvl as any).bitrate,
            name: (lvl as any).name,
          }));
          this.props.setQualityAvailable(true);
          this.props.setQualityLevels(levels);
          if (this.props.autoPlay) {
            const playPromise = video.play();
            if (
              hasValue(playPromise) &&
              typeof (playPromise as any).then === "function"
            ) {
              (playPromise as Promise<void>).catch(() => void 0);
            }
          }
        });
        // Selection changes handled via Redux in componentDidUpdate

        // Basic error handling: on fatal error try to recover once
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data) return;
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                this.destroyHls();
                this.safeSetState(() => ({ source: url })); // fallback to native if any
                break;
            }
          }
        });

        // Keep src undefined so React doesn't overwrite hls attachment
        if (this._isMounted) {
          this.safeSetState(() => ({ source: undefined, isHls: true }));
        }
        return;
      }

      // Last resort: set as src and let browser try
      if (this._isMounted) {
        this.safeSetState(() => ({ source: url, isHls: true }));
      }
      return;
    }

    // Non-HLS sources: set direct URL as the <video> src
    if (this._isMounted) {
      this.safeSetState(() => ({ source: url, isHls: false }));
    }
    this.props.setQualityAvailable(false);
    this.props.setQualityLevels([]);
  };

  public componentDidMount(): void {
    this._isMounted = true;
  }

  private handleViewedUpdate = () => {
    if (this._videoRef.current) {
      const duration = this.getResolvedDuration();
      if (!duration || duration <= 0) {
        return;
      }

      if (!this.state.hasPartialEnd) {
        if (this.currentTime >= duration * 0.8) {
          this.props.logIsPartialEnd(true);
          this.safeSetState(() => ({
            hasPartialEnd: true,
          }));
          return;
        }
      }
      if (!this.state.hasViewed60Percent) {
        if (this.currentTime >= duration * 0.6) {
          this.props.logViewedUpdate(true);
          this.safeSetState(() => ({
            hasViewed60Percent: true,
          }));
          return;
        }
      }
      if (!this.state.hasViewed40Percent) {
        if (this.currentTime >= duration * 0.4) {
          this.props.logViewedUpdate(true);
          this.safeSetState(() => ({
            hasViewed40Percent: true,
          }));
          return;
        }
      }
      if (!this.state.hasViewed20Percent) {
        if (this.currentTime >= duration * 0.2) {
          this.props.logViewedUpdate(true);
          this.safeSetState(() => ({
            hasViewed20Percent: true,
          }));
          return;
        }
      }
    }
  };

  private getResolvedDuration = (): number => {
    const htmlDuration = this._videoRef.current?.duration;
    if (
      typeof htmlDuration === "number" &&
      Number.isFinite(htmlDuration) &&
      htmlDuration > 0
    ) {
      return htmlDuration;
    }

    if (
      typeof this._hlsManifestDuration === "number" &&
      Number.isFinite(this._hlsManifestDuration) &&
      this._hlsManifestDuration > 0
    ) {
      return this._hlsManifestDuration;
    }

    const mediaDuration = this._hls?.media?.duration;
    if (
      typeof mediaDuration === "number" &&
      Number.isFinite(mediaDuration) &&
      mediaDuration > 0
    ) {
      return mediaDuration;
    }

    return 0;
  };
}

const mapStateToProps = (
  state: RootState,
): Pick<
  VideoProps,
  | "isLooped"
  | "isMuted"
  | "isPlaying"
  | "videoSource"
  | "volume"
  | "autoPlay"
  | "qualityAuto"
  | "qualityCurrentLevel"
> => ({
  isLooped: state.video.isLooped,
  isMuted: state.video.isMuted,
  isPlaying: state.video.isPlaying,
  videoSource: selectCurrentVideoSrc(state),
  volume: state.video.volume,
  autoPlay: state.video.autoPlay,
  qualityAuto: state.quality.auto,
  qualityCurrentLevel: state.quality.currentLevel,
});

const mapDispatchToProps = (
  dispatch: Dispatch,
): Pick<
  VideoProps,
  | "logIsEnded"
  | "logIsLoaded"
  | "logIsPlaying"
  | "logMediaError"
  | "playerError"
  | "logProgress"
  | "logTimeUpdate"
  | "logWaiting"
  | "pause"
  | "play"
  | "signAsset"
  | "logIsPartialEnd"
  | "logViewedUpdate"
  | "setQualityAvailable"
  | "setQualityLevels"
> => ({
  logIsEnded: bindActionCreators(
    VideoActions.actionCreators.logIsEnded,
    dispatch,
  ),
  logIsLoaded: bindActionCreators(
    VideoActions.actionCreators.logIsLoaded,
    dispatch,
  ),
  logIsPlaying: bindActionCreators(
    VideoActions.actionCreators.logIsPlaying,
    dispatch,
  ),
  logMediaError: bindActionCreators(
    EndpointActions.actionCreators.logMediaError,
    dispatch,
  ),
  playerError: bindActionCreators(
    EndpointActions.actionCreators.logPlayerError,
    dispatch,
  ),
  logProgress: bindActionCreators(
    VideoActions.actionCreators.logProgress,
    dispatch,
  ),
  logTimeUpdate: bindActionCreators(
    VideoActions.actionCreators.logTimeUpdate,
    dispatch,
  ),
  logWaiting: bindActionCreators(
    VideoActions.actionCreators.logWaiting,
    dispatch,
  ),
  pause: bindActionCreators(VideoActions.actionCreators.pause, dispatch),
  play: bindActionCreators(VideoActions.actionCreators.play, dispatch),
  signAsset: bindActionCreators(
    EndpointActions.actionCreators.signAsset,
    dispatch,
  ),
  logIsPartialEnd: bindActionCreators(
    VideoActions.actionCreators.logIsPartialEnd,
    dispatch,
  ),
  logViewedUpdate: bindActionCreators(
    VideoActions.actionCreators.logViewedUpdate,
    dispatch,
  ),
  setQualityAvailable: bindActionCreators(
    QualityActions.actionCreators.setAvailable,
    dispatch,
  ),
  setQualityLevels: bindActionCreators(
    QualityActions.actionCreators.setLevels,
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps, undefined, {
  forwardRef: true,
})(Video);
